export type HomeRoomId = "nz-lobby" | "reo-maori";
export type HomeModule = "conversation" | "rooms" | "game" | "safety";
export type HomePreviewPhase = "idle" | "focused" | "selected" | "updating" | "settled";

export type HomeStory = {
  label: string;
  roomId: HomeRoomId;
  roomLabel: string;
  quote: string;
  author: string;
  safetyTitle: string;
  safetyCopy: string;
};

export type HomePreviewState = {
  activeModule: HomeModule;
  attentionModule: HomeModule | null;
  selectedRoom: HomeRoomId;
  storyIndex: number;
  roomLinkPending: boolean;
  phase: HomePreviewPhase;
  revision: number;
  presenceRevision: number;
};

export type HomePresencePreview = {
  id: string;
  nickname: string;
  avatar: string;
  presenceStatus?: "online" | "busy";
};

export type HomePresenceRoom = {
  memberPreviews?: readonly HomePresencePreview[];
};

export function homeOrbitMembers(rooms: readonly HomePresenceRoom[], limit = 18) {
  const queues = rooms.map((room) => [...(room.memberPreviews ?? [])]);
  const seen = new Set<string>();
  const members: HomePresencePreview[] = [];
  while (members.length < limit && queues.some((queue) => queue.length > 0)) {
    for (const queue of queues) {
      let member = queue.shift();
      while (member && seen.has(member.id)) member = queue.shift();
      if (!member) continue;
      seen.add(member.id);
      members.push(member);
      if (members.length === limit) break;
    }
  }
  return members;
}

export type HomePreviewEvent =
  | { type: "focus"; module: HomeModule }
  | { type: "blur" }
  | { type: "selectRoom"; roomId: HomeRoomId }
  | { type: "selectRooms" }
  | { type: "selectConversation" }
  | { type: "selectGame" }
  | { type: "openSafety" }
  | { type: "changeExample" }
  | { type: "beginUpdate" }
  | { type: "settle" }
  | { type: "presenceUpdated" };

export const HOME_STORIES: readonly HomeStory[] = [
  {
    label: "Lobby weather conversation",
    roomId: "nz-lobby",
    roomLabel: "New Zealand Lobby",
    quote: "Weather looks kinder on Sunday — bring a jacket anyway.",
    author: "Ana",
    safetyTitle: "Control what you see",
    safetyCopy: "Hide or report a member, or turn on Focus when the room gets busy.",
  },
  {
    label: "Te reo greeting",
    roomId: "reo-maori",
    roomLabel: "Te Reo Māori Room",
    quote: "Kia ora koutou! Kei te pēhea tō koutou rā?",
    author: "Aroha",
    safetyTitle: "Learners are welcome",
    safetyCopy: "Clear language guidance helps learners keep the room in te reo Māori.",
  },
  {
    label: "Lobby introduction",
    roomId: "nz-lobby",
    roomLabel: "New Zealand Lobby",
    quote: "Good call. I’m new here — hello from Kirikiriroa.",
    author: "Ravi",
    safetyTitle: "Moderation decisions are recorded",
    safetyCopy: "Reports, moderation reasons and appeals stay connected and reviewable.",
  },
  {
    label: "Te reo learner conversation",
    roomId: "reo-maori",
    roomLabel: "Te Reo Māori Room",
    quote: "Kei te ako tonu au. He pai ki ahau tēnei kōrero.",
    author: "Mere",
    safetyTitle: "Room purpose stays clear",
    safetyCopy: "The room asks for te reo Māori while giving learners a clear, respectful place to practise.",
  },
] as const;

// Simplified from Natural Earth 1:50m public-domain coastline data. Keeping
// the geographic silhouette here lets the hero and quiet-room view share one
// recognisable Aotearoa outline without shipping a map library.
export const AOTEAROA_MAP_PATH = "M97.8 127.4L103.6 124.4 108.5 121.3 107.7 123.2 106.6 127.3 108.6 124.3 110.2 122.7 112.1 123.1 111.8 125.4 112.8 125.6 112.2 128 110.1 130.7 111.4 133.9 112 137.2 107.8 143.4 100.8 154.2 94.9 159 91.5 162 91.6 164.3 93 164.9 95.7 168.2 97.5 171.4 94 171.8 89.6 171.2 89 169.7 87.3 171.8 84.2 169.6 83.2 173.8 79.1 176.1 75.3 178.4 73.6 183 70.6 189.7 70.5 193.8 66.8 202.9 67.9 205.7 63.2 207.3 56.7 214.1 49.3 219 41.9 218 37 218.7 36.2 216 33.1 214.4 28 211.6 23.9 212.5 15.5 210.6 14.7 209.1 14.2 207.6 12.9 205.1 16.8 203.2 17.5 200.6 18.2 198.2 19.9 197.8 21.1 196.4 19.6 195 21.6 192.8 21.7 190.6 25.4 189 26.6 186.8 30.9 185.5 32.3 180.2 40.4 173.6 45.8 171.7 47.1 170.2 54.8 166.1 56.4 164.8 59.1 161.5 61.6 159.4 61.7 158.8 65.6 158.5 66.3 156.3 68.6 154.3 70.8 154.9 73.1 148.4 74.4 147.2 75.3 141.1 79.3 135.4 83.6 130.3 89.5 116.2 95.6 114.4 92.9 117.9 96.9 121.8 97.8 127.4ZM33.9 223.1L33.9 223.8 32.6 223.6 32.6 224.3 33.7 224.7 34.1 225.3 35.2 225.1 35.4 225.9 35.2 226.7 34.4 227.2 32.3 227.5 30.8 228.6 29.6 228.4 29.3 228.5 27.9 229.6 26.3 230 25.9 229.9 26.1 228.9 27.3 227.9 27.3 227 27.6 226.2 28.7 225.7 28.7 224.7 29.5 223.9 29 222 29.3 220.3 31.5 220.2 33.9 223.1ZM129 41.7L129.1 42.7 128.2 42.3 127.8 41.6 126.7 40.9 126.5 40.7 126.4 39.2 127 38.6 127.1 38.3 127.3 38.1 127.8 38.9 128.6 40 129 41.7ZM99.8 18.7L102.1 17.1 105.8 20.7 108.2 20.8 110.7 24.3 113.3 24 114.6 26.8 115.7 31.2 114.3 33.5 119.5 42.2 119.7 47.4 118.5 51.3 121 51.8 125.9 54 128 57.8 128.4 51.8 127.9 47.8 128 45.1 132 49.5 133.9 57.6 135.6 63.7 137.4 65.5 144.8 69.3 153.6 70.5 158.2 65.3 164.1 63.8 167.3 67.1 164.7 78.8 160.3 83.6 160 90 158.4 90.8 153 89.7 148.2 92.9 147.3 98.7 144.8 109.4 139.9 116.9 135.7 124.9 127.8 131.9 124.7 131.8 120.8 130.1 120.8 126.8 120 127.6 117.4 127.6 122.3 120 124.8 108.9 118 102.9 111.1 98.1 106.2 93.8 107.2 90.7 114.3 87.7 117.1 81.6 120 71.6 121.1 67.9 118.8 60.8 116.7 55.7 118.8 56.6 119.2 53.1 116.1 53.6 114.1 49.4 114.7 46.6 113.7 43.4 113.1 39.8 112.5 41.2 108 36.1 111.3 42.5 109.1 41 103.9 26.4 103.3 25.4 101.2 28.4 99.9 25.6 98.8 21.9 94.5 13.5 95.9 11.8 98.7 17.3 99.8 18.7Z";

function firstStoryForRoom(roomId: HomeRoomId) {
  const index = HOME_STORIES.findIndex((story) => story.roomId === roomId);
  return index < 0 ? 0 : index;
}

export function nextHomeStory(index: number, roomId?: HomeRoomId) {
  if (!roomId) return (index + 1) % HOME_STORIES.length;
  for (let offset = 1; offset <= HOME_STORIES.length; offset += 1) {
    const candidate = (index + offset) % HOME_STORIES.length;
    if (HOME_STORIES[candidate]?.roomId === roomId) return candidate;
  }
  return firstStoryForRoom(roomId);
}

export function initialHomePreviewState(roomId: HomeRoomId = "nz-lobby"): HomePreviewState {
  return {
    activeModule: "conversation",
    attentionModule: null,
    selectedRoom: roomId,
    storyIndex: firstStoryForRoom(roomId),
    roomLinkPending: false,
    phase: "idle",
    revision: 0,
    presenceRevision: 0,
  };
}

export function reduceHomePreview(state: HomePreviewState, event: HomePreviewEvent): HomePreviewState {
  switch (event.type) {
    case "focus":
      return { ...state, attentionModule: event.module, phase: "focused" };
    case "blur":
      return { ...state, attentionModule: null, phase: state.revision > 0 ? "settled" : "idle" };
    case "selectRoom": {
      const currentStory = HOME_STORIES[state.storyIndex];
      const storyIndex = currentStory?.roomId === event.roomId ? state.storyIndex : firstStoryForRoom(event.roomId);
      return {
        ...state,
        activeModule: "rooms",
        attentionModule: "rooms",
        selectedRoom: event.roomId,
        storyIndex,
        roomLinkPending: true,
        phase: "selected",
        revision: state.revision + 1,
      };
    }
    case "selectRooms":
      return {
        ...state,
        activeModule: "rooms",
        attentionModule: "rooms",
        roomLinkPending: false,
        phase: "selected",
        revision: state.revision + 1,
      };
    case "selectConversation":
      return { ...state, activeModule: "conversation", attentionModule: "conversation", roomLinkPending: false, phase: "selected", revision: state.revision + 1 };
    case "selectGame":
      return { ...state, activeModule: "game", attentionModule: "game", roomLinkPending: false, phase: "selected", revision: state.revision + 1 };
    case "openSafety":
      return { ...state, activeModule: "safety", attentionModule: "safety", roomLinkPending: false, phase: "selected", revision: state.revision + 1 };
    case "changeExample":
      return {
        ...state,
        activeModule: "conversation",
        attentionModule: "conversation",
        storyIndex: nextHomeStory(state.storyIndex, state.selectedRoom),
        roomLinkPending: false,
        phase: "selected",
        revision: state.revision + 1,
      };
    case "beginUpdate":
      return {
        ...state,
        activeModule: state.roomLinkPending ? "conversation" : state.activeModule,
        attentionModule: state.roomLinkPending ? "conversation" : state.attentionModule,
        phase: "updating",
      };
    case "settle":
      return { ...state, attentionModule: null, roomLinkPending: false, phase: "settled" };
    case "presenceUpdated":
      return { ...state, presenceRevision: state.presenceRevision + 1 };
  }
}

export function homeMarkup() {
  return `
    <section class="home-screen" id="home-screen" data-phase="idle" data-active-module="conversation" data-selected-room="nz-lobby" data-revealed="false" data-hero-stage="earth" aria-labelledby="home-title">
      <section class="home-hero" aria-describedby="home-summary">
        <div class="home-motion-field" aria-hidden="true">
          <span class="home-stars home-stars-near"></span>
          <span class="home-stars home-stars-cluster"></span>
          <span class="home-stars home-stars-far"></span>
          <span class="home-haze home-haze-a"></span>
          <span class="home-haze home-haze-b"></span>
          <svg class="home-routes" viewBox="0 0 1600 760" preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="home-route-a" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stop-color="#77bdf2" stop-opacity="0"/>
                <stop offset=".48" stop-color="#69dfd2" stop-opacity=".88"/>
                <stop offset="1" stop-color="#77bdf2" stop-opacity="0"/>
              </linearGradient>
            </defs>
            <path class="home-route route-a" pathLength="1" stroke="url(#home-route-a)" d="M-120 598C248 492 374 236 724 302s554 344 1032-62"/>
            <path class="home-route route-b" pathLength="1" stroke="url(#home-route-a)" d="M168-72c156 252 302 310 518 226s404-138 616 24 226 342 510 294"/>
            <path class="home-route route-c" pathLength="1" stroke="url(#home-route-a)" d="M792 824c86-292 224-390 474-430s356-124 482-358"/>
          </svg>
        </div>

        <div class="home-hero-copy">
          <p class="home-presence"><span class="home-presence-dot"></span><strong id="home-total-online">0</strong><span id="home-total-online-label">people across the public rooms</span></p>
          <h1 id="home-title">Come for one conversation. Find a place worth returning to.</h1>
          <p id="home-summary">ManyHellos is an adults-only public chat community with open rooms, shared games, a moderated message wall and human review of safety reports.</p>
          <p class="home-beta-note"><strong>BETA</strong><span>We’re newly open, so rooms may be quiet. Everyone shown online is a real participant—no activity bots.</span></p>
          <div class="home-actions">
            <button class="home-button home-button-primary" id="home-enter" type="button">Enter the New Zealand Lobby <span aria-hidden="true">→</span></button>
            <button class="home-button home-button-secondary" id="home-register" type="button">Create an account</button>
          </div>
          <div class="home-age-notice" role="note" aria-label="Adults only. Everyone who joins must confirm they are 18 or older. ManyHellos does not verify identity.">
            <span class="home-age-badge" aria-hidden="true"><span class="home-age-badge-mark">18+</span></span>
            <p class="home-boundary"><strong>18+ only</strong><span>Everyone who joins must confirm they are 18 or older. ManyHellos does not verify identity. Guests can read and join public chat; an account adds a chosen nickname, progress and saved features.</span></p>
          </div>
        </div>

        <button class="home-orbit" id="home-orbit" type="button" aria-label="Replay the globe-to-Aotearoa locator.">
          <span class="home-orbit-water" aria-hidden="true"></span>
          <span class="home-orbit-ring orbit-ring-a" aria-hidden="true"></span>
          <span class="home-orbit-ring orbit-ring-b" aria-hidden="true"></span>
          <span class="home-orbit-core">
            <svg class="home-earth" viewBox="0 0 160 160" aria-hidden="true">
              <defs>
                <radialGradient id="home-earth-ocean" cx="35%" cy="24%" r="78%">
                  <stop offset="0" stop-color="#8de7ee"/>
                  <stop offset=".34" stop-color="#318eb4"/>
                  <stop offset=".72" stop-color="#0b4c7d"/>
                  <stop offset="1" stop-color="#052744"/>
                </radialGradient>
                <linearGradient id="home-earth-land" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stop-color="#b9d89a"/>
                  <stop offset=".55" stop-color="#66aa82"/>
                  <stop offset="1" stop-color="#2f795f"/>
                </linearGradient>
                <clipPath id="home-earth-clip"><circle cx="80" cy="80" r="69"/></clipPath>
              </defs>
              <g class="home-earth-world">
                <circle class="home-earth-ocean" cx="80" cy="80" r="69" fill="url(#home-earth-ocean)"/>
                <g clip-path="url(#home-earth-clip)">
                  <ellipse class="home-earth-grid" cx="80" cy="80" rx="40" ry="69"/>
                  <ellipse class="home-earth-grid" cx="80" cy="80" rx="68" ry="24"/>
                  <ellipse class="home-earth-grid" cx="80" cy="80" rx="68" ry="47"/>
                  <g class="home-earth-map">
                    <path class="home-earth-land" d="M14 33c12-12 28-17 42-13l13 8 11 1 8 10-7 9-12 1-7 9-12 2-7 13-11-2-4-13-10-8-3-10Z"/>
                    <path class="home-earth-land" d="M48 73l13 5 7 11-1 15-8 13-5 23-8 5-7-16-7-13 5-17-5-10Z"/>
                    <path class="home-earth-land" d="M82 34l12-7 17 3 9 8 17 1 17 12-3 9-18 3-10 9-20-2-8-9-13-5Z"/>
                    <path class="home-earth-land" d="M95 67l18 1 12 13-2 18-12 24-13-4-8-19 3-14-5-10Z"/>
                    <path class="home-earth-land" d="M128 96l14-5 12 5 5 12-8 13-18 3-12-10 2-11Z"/>
                    <path class="home-earth-land home-earth-aotearoa" d="M148 116l3 2-2 8-4 7-2-2 3-8Zm5-10 2 2-1 6-3-1Z"/>
                  </g>
                  <ellipse class="home-earth-glint" cx="54" cy="42" rx="25" ry="13" transform="rotate(-28 54 42)"/>
                </g>
                <circle class="home-earth-rim" cx="80" cy="80" r="69"/>
              </g>
              <g class="home-earth-locator">
                <circle cx="133" cy="120" r="9"/>
                <circle class="home-earth-locator-pulse" cx="133" cy="120" r="3"/>
                <path d="M133 104v8m0 16v8m-16-16h8m16 0h8"/>
              </g>
            </svg>
            <svg class="home-aotearoa-map" viewBox="0 0 180 240" aria-hidden="true">
              <path class="home-aotearoa-route" d="M149 18C143 52 137 82 119 111S72 175 18 215"/>
              <path class="home-aotearoa-island" d="${AOTEAROA_MAP_PATH}"/>
              <circle class="home-aotearoa-fix" cx="119" cy="128" r="5"/>
              <circle class="home-aotearoa-fix-ring" cx="119" cy="128" r="12"/>
            </svg>
            <span class="home-aotearoa-label" aria-hidden="true"><strong>Aotearoa</strong><small>located</small></span>
          </span>
          <span class="home-orbit-nodes" id="home-orbit-nodes" aria-hidden="true"></span>
          <span class="home-orbit-ping ping-a" aria-hidden="true"></span>
          <span class="home-orbit-ping ping-b" aria-hidden="true"></span>
          <span class="home-orbit-caption" id="home-orbit-caption" aria-hidden="true">Live room count</span>
        </button>
      </section>

      <section class="home-modules" aria-labelledby="home-modules-title">
        <header class="home-section-heading">
          <div>
            <h2 id="home-modules-title">Choose what you want to see.</h2>
            <p>Room counts come from the live service. Conversation previews are illustrative and change only when you ask.</p>
          </div>
          <button class="home-refresh" id="home-refresh" data-home-preview-target="conversation" type="button" aria-describedby="home-preview-status">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 7v5h-5M4 17v-5h5"/><path d="M18.2 9A7 7 0 0 0 6.4 6.4L4 9M5.8 15A7 7 0 0 0 17.6 17.6L20 15"/></svg>
            Change the conversation preview
          </button>
        </header>

        <div class="home-preview-tabs" role="group" aria-label="Choose a homepage preview">
          <button type="button" data-home-select="conversation" aria-label="Conversation" aria-pressed="true"><span class="home-tab-full">Conversation</span><span class="home-tab-compact" aria-hidden="true">Chat</span></button>
          <button type="button" data-home-select="rooms" aria-pressed="false">Rooms</button>
          <button type="button" data-home-select="game" aria-pressed="false">Game</button>
          <button type="button" data-home-select="safety" aria-pressed="false">Safety</button>
        </div>

        <p class="sr-only" id="home-preview-status" aria-live="polite" aria-atomic="true"></p>
        <div class="home-module-system" id="home-module-system">
          <svg class="home-module-route" viewBox="0 0 1000 580" preserveAspectRatio="none" aria-hidden="true">
            <path data-route-target="rooms" d="M300 292 C430 116 508 102 620 126"/>
            <path data-route-target="safety" d="M300 292 C430 448 510 462 620 452"/>
            <path data-route-target="game" d="M300 292 C520 292 690 292 845 292"/>
            <path class="home-room-signal" data-room-signal="nz-lobby" pathLength="1" d="M620 126 C508 102 430 116 300 260"/>
            <path class="home-room-signal" data-room-signal="reo-maori" pathLength="1" d="M620 198 C510 198 430 214 300 318"/>
            <circle cx="300" cy="292" r="6"/>
            <circle data-route-node="rooms" cx="620" cy="126" r="7"/>
            <circle data-route-node="safety" cx="620" cy="452" r="7"/>
            <circle data-route-node="game" cx="845" cy="292" r="7"/>
          </svg>

          <article class="home-module home-conversation-module" data-home-module="conversation" aria-labelledby="home-conversation-heading">
            <div class="home-module-meta"><span id="home-story-room">New Zealand Lobby</span><span class="home-live-label"><i></i> illustrative</span></div>
            <h3 class="sr-only" id="home-conversation-heading">Conversation preview</h3>
            <div class="home-story-stage" id="home-story-stage">
              <div class="home-story-content" id="home-story-content">
                <blockquote id="home-story-quote">“Weather looks kinder on Sunday — bring a jacket anyway.”</blockquote>
                <p class="home-story-author"><span id="home-story-initials">AN</span><strong id="home-story-author">Ana</strong><small>Illustrative public message</small></p>
              </div>
            </div>
            <button class="home-text-action" id="home-story-enter" data-home-preview-target="conversation" type="button">Enter this room <span aria-hidden="true">→</span></button>
          </article>

          <article class="home-module home-room-module" data-home-module="rooms" aria-labelledby="home-rooms-heading">
            <div class="home-module-meta"><span id="home-rooms-heading">Public rooms</span><span>2 open</span></div>
            <button class="home-room-row" data-home-room="nz-lobby" data-home-preview-target="rooms" type="button" aria-pressed="true"><span><strong>New Zealand Lobby</strong><small>Open conversation</small></span><b id="home-nz-count">0</b></button>
            <button class="home-room-row" data-home-room="reo-maori" data-home-preview-target="rooms" type="button" aria-pressed="false"><span><strong>Te Reo Māori Room</strong><small>Learners welcome</small></span><b id="home-maori-count">0</b></button>
            <p id="home-room-selection">The preview is showing the New Zealand Lobby.</p>
          </article>

          <article class="home-module home-game-module" data-home-module="game" aria-labelledby="home-game-heading">
            <img src="/game-assets/harbour-ledger-twilight.webp" width="1672" height="940" loading="lazy" decoding="async" alt="Original illustration of a harbour town at twilight" />
            <div class="home-game-shade"></div>
            <div class="home-game-copy">
              <span>Text mystery · 3–6 players</span>
              <h3 id="home-game-heading">The Missing Harbour Ledger</h3>
              <p>Work through one short mystery together by asking questions and comparing clues.</p>
              <button class="home-text-action" id="home-game" data-home-preview-target="game" type="button">Open the mystery table <span aria-hidden="true">→</span></button>
            </div>
          </article>

          <article class="home-module home-safety-module" data-home-module="safety" aria-labelledby="home-safety-title">
            <div class="home-module-meta"><span>Safety system</span><span>People decide</span></div>
            <span class="home-safety-mark" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M12 3 20 6v5c0 5.1-3.4 8.2-8 10-4.6-1.8-8-4.9-8-10V6l8-3Z"/><path d="m8.5 12 2.2 2.2 4.8-5"/></svg></span>
            <div class="home-safety-content" id="home-safety-content">
              <h3 id="home-safety-title">Control what you see</h3>
              <p id="home-safety-copy">Hide or report a member, or turn on Focus when the room gets busy.</p>
            </div>
            <button class="home-text-action" id="home-safety" data-home-preview-target="safety" type="button">Read rules, privacy and appeals <span aria-hidden="true">→</span></button>
          </article>
        </div>
      </section>

      <section class="home-community" aria-labelledby="home-community-title">
        <div class="home-community-copy">
          <h2 id="home-community-title">Private feedback and the public community wall.</h2>
          <p>Send a private note to the site team, or leave a public note on the community wall. Public wall posts must not include links or contact details and can be reported.</p>
        </div>
        <div class="home-community-actions">
          <button class="home-community-action" id="home-feedback" type="button">
            <span class="home-community-symbol" aria-hidden="true">↘</span>
            <span><strong>Private feedback</strong><small>Ideas, problems or something you want the team to know.</small></span>
            <em>Site team only</em>
          </button>
          <button class="home-community-action" id="home-wall" type="button">
            <span class="home-community-symbol" aria-hidden="true">✦</span>
            <span><strong>Community wall</strong><small>Public notes and signatures, reviewed under the same rules as chat.</small></span>
            <em>No contact details</em>
          </button>
        </div>
      </section>

      <section class="home-close" aria-label="ManyHellos principles">
        <div>
          <h2>Come back when you feel like another conversation.</h2>
          <button class="home-button home-button-primary" id="home-enter-bottom" type="button">Enter the New Zealand Lobby <span aria-hidden="true">→</span></button>
        </div>
        <dl>
          <div><dt>Real activity only</dt><dd>Room counts come from connected people. No simulated members or automated posts.</dd></div>
          <div><dt>Recognition without extra reach</dt><dd>Registered members can unlock expression and recognition, never reach or moderation power.</dd></div>
          <div><dt>Human review</dt><dd>Automation may pause immediate harm. People review context, reasons and appeals.</dd></div>
        </dl>
      </section>

      <footer class="home-footer">
        <p>© 2026 ManyHellos. All rights reserved.</p>
        <nav aria-label="Site information">
          <button id="home-footer-info" type="button">Rules &amp; privacy</button>
          <a href="/privacy.html">Privacy notice</a>
          <a href="/legal-content-complaint.html">Formal content complaints</a>
        </nav>
      </footer>
    </section>`;
}
